import { Button, Input, Tabs, TabsList, TabsTrigger } from '@/components/ui';
import {
  Card,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Form, FormField, FormItem, FormLabel } from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import TooltipWrapper from '@/components/ui/tooltip';
import { privateApi } from '@/lib/api-clients/rest-client';
import { cn } from '@/lib/utils';
import { useAccountStore } from '@/store/use-account-store';
import { ErrorCode, ErrorMsgs } from '@/types';
import { imgFromAbbr, abbrFromSym } from '@/utils/token-symbol';
import { bigIntToDecimalStr, parseDecimalToBigInt } from '@/utils/value-format';
import { zodResolver } from '@hookform/resolvers/zod';
import { Mail, Send } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

const formSchema = z.object({
  ticker: z.string().min(1, 'Ticker is required.'),
  depositSize: z.string(),
  marginRequested: z.string(),
  contactPreference: z.enum(['email', 'telegram']),
  email: z.string().email().or(z.literal('')),
  telegram: z.string().or(z.literal('')),
  custody: z.string().min(1, 'Custody is required.'),
  duration: z.string().min(1, 'Duration is required.'),
});

type FormSchema = z.infer<typeof formSchema>;

export default function LockedCollateralAssetForm() {
  const [isCustomDuration, setIsCustomDuration] = useState(false);
  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      ticker: '',
      depositSize: '',
      marginRequested: '',
      contactPreference: 'email',
      email: '',
      telegram: '',
      custody: '',
      duration: undefined,
    },
  });

  async function onSubmit(data: FormSchema) {
    const {
      contactPreference,
      marginRequested,
      depositSize,
      ticker,
      custody,
      duration,
      ...contactFields
    } = data;

    const promise = privateApi.post('/transfer/lockedAsset', {
      size: parseFloat(depositSize),
      margin: parseFloat(marginRequested),
      contact: contactFields[contactPreference],
      custody,
      duration,
      ticker,
    });

    toast.promise(promise, {
      success: 'The request has been submited.',
      loading: 'Submitting...',
      error: (err) =>
        `Couldn't submit the request: ${err in ErrorMsgs ? ErrorMsgs[err as ErrorCode] : 'Unknown error.'}`,
    });

    promise.then(() => form.reset());

    await promise;
  }

  // const { data } = useExchangeInfo();

  // const symbols = useMemo(() => {
  //   if (!data?.symbols) return [];
  //   return data.symbols.sort((a, b) =>
  //     a.displayName.localeCompare(b.displayName),
  //   );
  // }, [data?.symbols]);

  // const ticker = form.watch('ticker');
  const contactPreference = form.watch('contactPreference');

  const { isValid, isSubmitting } = form.formState;

  const hasAuth = useAccountStore((state) => state.hasAuth);

  return (
    <Card className="w-full max-w-3xl">
      <CardHeader>
        <CardTitle>Collateral on locked assets</CardTitle>
        <CardDescription>
          Vest now offers collateral on locked assets. To qualify, the asset
          must have liquid spot market and the depositor must be able to verify
          ownership of the locked asset.
        </CardDescription>
        <CardDescription>
          To start the process of using locked assets as margin collateral on
          Vest, please submit the form below.
        </CardDescription>
      </CardHeader>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="grid grid-cols-2 gap-4 px-6 pb-6">
            <FormField
              control={form.control}
              name="ticker"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-normal text-vestgrey-100">
                    Asset Ticker
                  </FormLabel>
                  <Input
                    {...field}
                    placeholder="Enter ticker"
                    className="h-11 rounded-none bg-vestgrey-800 text-base transition-colors duration-100 placeholder:text-muted-foreground group-hover:bg-border"
                  />
                  {/* <Select
                    value={field.value}
                    onValueChange={(value) => field.onChange(value)}
                  >
                    <SelectTrigger
                      className={cn(
                        'h-11',
                        !field.value && 'text-vestgrey-100',
                      )}
                    >
                      <SelectValue
                        className="text-base"
                        placeholder="Select symbol"
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {symbols.map(({ symbol, displayName }) => (
                        <SelectItem
                          className="py-3 text-base leading-none"
                          value={symbol}
                          key={symbol}
                        >
                          <img
                            src={imgFromAbbr(abbrFromSym(symbol))}
                            alt="Token Image"
                            className="h-4 w-4"
                          />
                          {displayName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select> */}
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="custody"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-normal text-vestgrey-100">
                    Custody
                  </FormLabel>
                  <Input
                    {...field}
                    placeholder="Enter custody"
                    className="h-11 rounded-none bg-vestgrey-800 text-base transition-colors duration-100 placeholder:text-muted-foreground group-hover:bg-border"
                  />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="depositSize"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-normal text-vestgrey-100">
                    Deposit Size
                  </FormLabel>
                  <div className="group relative flex">
                    <Input
                      {...field}
                      onChange={(e) => {
                        let { value } = e.target;
                        value = value.replace(/[^0-9.]/g, '');
                        const firstDotIndex = value.indexOf('.');
                        if (firstDotIndex === -1) {
                          field.onChange(value);
                        } else {
                          const before = value.slice(0, firstDotIndex + 1);
                          const after = value
                            .slice(firstDotIndex + 1)
                            .replace(/\./g, '');
                          field.onChange(before + after);
                        }
                      }}
                      // onBlur={(e) => {
                      //   const { marketSpec } =
                      //     useMarketStore(symbol).getState();
                      //   field.onChange(
                      //     bigIntToDecimalStr(
                      //       parseDecimalToBigInt(
                      //         e.target.value,
                      //         marketSpec.sizeDecimals,
                      //       ),
                      //       marketSpec.sizeDecimals,
                      //     ),
                      //   );
                      //   field.onBlur();
                      // }}
                      className="h-11 rounded-none bg-vestgrey-800 text-base transition-colors duration-100 placeholder:text-muted-foreground group-hover:bg-border"
                      placeholder="0"
                    />
                    {/* {symbol && ( */}
                    <div className="flex h-11 items-center bg-vestgrey-800 px-4 text-foreground duration-100 group-hover:bg-border">
                      {/* {abbrFromSym(symbol)} */}
                      Coins
                    </div>
                    {/* )} */}
                  </div>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="marginRequested"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-normal text-vestgrey-100">
                    Margin Requested
                  </FormLabel>
                  <div className="group relative flex">
                    <Input
                      {...field}
                      onBlur={(e) => {
                        const { value } = e.target;
                        if (value === '') return;
                        field.onChange(
                          bigIntToDecimalStr(parseDecimalToBigInt(value, 2), 2),
                        );
                        field.onBlur();
                      }}
                      className="h-11 rounded-none bg-vestgrey-800 text-base transition-colors duration-100 placeholder:text-muted-foreground group-hover:bg-border"
                      placeholder="0.00"
                    />
                    <div className="flex h-11 items-center bg-vestgrey-800 px-4 text-foreground duration-100 group-hover:bg-border">
                      USD
                    </div>
                  </div>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="duration"
              render={({ field }) => (
                <FormItem className="col-span-full">
                  <FormLabel className="text-sm font-normal text-vestgrey-100">
                    Duration
                  </FormLabel>
                  <Select
                    onValueChange={(value) => {
                      if (value === 'Custom') {
                        setIsCustomDuration(true);
                        field.onChange('');
                      } else {
                        setIsCustomDuration(false);
                        field.onChange(value);
                      }
                    }}
                  >
                    <SelectTrigger
                      className={cn(
                        'h-11 rounded-none border-0 bg-vestgrey-800',
                        !field.value &&
                          !isCustomDuration &&
                          'text-vestgrey-100',
                      )}
                    >
                      <SelectValue
                        className="text-base"
                        placeholder="Select duration"
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {['6 months', '1 year', 'Custom'].map((value) => (
                        <SelectItem
                          className="py-3 text-base leading-none"
                          value={value}
                          key={value}
                        >
                          {value}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {isCustomDuration && (
                    <Input
                      {...field}
                      autoFocus
                      placeholder="Enter duration"
                      className="h-11 rounded-none bg-vestgrey-800 text-base transition-colors duration-100 placeholder:text-muted-foreground group-hover:bg-border"
                    />
                  )}
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="contactPreference"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-normal text-vestgrey-100">
                    Contact Preference
                  </FormLabel>
                  <Tabs
                    className="h-11"
                    value={field.value}
                    onValueChange={(value) => {
                      form.setValue(
                        value === 'email' ? 'telegram' : 'email',
                        '',
                      );
                      field.onChange(value);
                    }}
                  >
                    <TabsList className="grid h-full grid-cols-2">
                      <TabsTrigger className="h-full gap-2 px-0" value="email">
                        <Mail size={14} />
                        Email
                      </TabsTrigger>
                      <TabsTrigger
                        className="h-full gap-2 px-0"
                        value="telegram"
                      >
                        <Send size={14} />
                        Telegram
                      </TabsTrigger>
                    </TabsList>
                  </Tabs>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name={contactPreference}
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-normal text-vestgrey-100">
                    {contactPreference === 'email'
                      ? 'Email Address'
                      : 'Username'}
                  </FormLabel>
                  <Input
                    {...field}
                    className="h-11 rounded-none bg-vestgrey-800 text-base transition-colors placeholder:text-muted-foreground hover:bg-border"
                    placeholder={
                      contactPreference === 'email'
                        ? 'example@mail.com'
                        : 'exampleusername'
                    }
                  />
                </FormItem>
              )}
            />
          </div>
          <CardFooter className="justify-end">
            {!hasAuth ? (
              <TooltipWrapper
                direction="left"
                content="Connect wallet to submit."
              >
                <Button
                  disabled
                  size="lg"
                  className="rounded-none px-4 font-mono uppercase"
                >
                  Submit
                </Button>
              </TooltipWrapper>
            ) : (
              <Button
                disabled={
                  !isValid ||
                  isSubmitting ||
                  form.watch(contactPreference) === ''
                }
                size="lg"
                className="rounded-none px-4 font-mono uppercase"
              >
                Submit
              </Button>
            )}
          </CardFooter>
        </form>
      </Form>
    </Card>
  );
}
